import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ConsultationCTA from '../../components/ui/ConsultationCTA';
import ServiceHero from './components/ServiceHero';
import ServiceOverview from './components/ServiceOverview';
import ConsultationForm from './components/ConsultationForm';
import RelatedCaseStudies from './components/RelatedCaseStudies';
import TeamExpertise from './components/TeamExpertise';
import CapabilitiesMatrix from './components/CapabilitiesMatrix';
import FAQSection from './components/FAQSection';
import SuccessMetrics from './components/SuccessMetrics';
import RelatedServices from './components/RelatedServices';

const IndividualServiceDetail = () => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mock service data - in real app this would come from props/API
  const serviceData = {
    id: 'ai-models',
    category: 'AI Engineering',
    title: 'Custom AI Models Development',
    description: `Transform your business with cutting-edge artificial intelligence solutions tailored to your specific needs. Our expert team develops custom AI models that drive innovation, automate processes, and unlock new opportunities for growth.`,
    heroImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
    keyBenefits: ['Faster Decision Making', 'Process Automation', 'Competitive Advantage'],
    successRate: '95%',
    detailedDescription: `Our Custom AI Models Development service combines deep technical expertise with business acumen to create intelligent solutions that solve real-world problems. We specialize in machine learning, deep learning, natural language processing, and computer vision technologies to build AI systems that integrate seamlessly with your existing infrastructure.\n\nFrom initial concept to deployment and maintenance, our team guides you through every step of the AI development lifecycle. We ensure that your custom AI models are not only technically sound but also aligned with your business objectives and scalable for future growth.`,
    deliverables: [
      'Custom-trained AI models optimized for your data',
      'Comprehensive model documentation and API integration',
      'Performance monitoring and optimization tools',
      'Training materials and knowledge transfer sessions',
      'Ongoing support and maintenance packages'
    ],
    features: [
      'Multi-platform deployment capabilities',
      'Real-time inference and batch processing',
      'Scalable cloud-native architecture',
      'Advanced security and privacy protection',
      'Continuous learning and model improvement'
    ],
    methodology: [
      {
        title: 'Discovery & Requirements Analysis',
        description: 'We begin by understanding your business challenges, data landscape, and success criteria to define the optimal AI solution approach.',
        activities: ['Stakeholder interviews', 'Data assessment', 'Technical feasibility study', 'ROI analysis']
      },
      {
        title: 'Data Preparation & Model Design',
        description: 'Our team prepares your data and designs the AI model architecture that best fits your requirements and constraints.',
        activities: ['Data cleaning & preprocessing', 'Feature engineering', 'Model architecture design', 'Training strategy development']
      },
      {
        title: 'Model Development & Training',
        description: 'We develop and train your custom AI models using state-of-the-art techniques and best practices for optimal performance.',
        activities: ['Model implementation', 'Hyperparameter tuning', 'Cross-validation testing', 'Performance optimization']
      },
      {
        title: 'Testing & Validation',
        description: 'Rigorous testing ensures your AI models meet performance standards and work reliably in production environments.',
        activities: ['Model validation', 'A/B testing', 'Performance benchmarking', 'Security testing']
      },
      {
        title: 'Deployment & Integration',
        description: 'We deploy your AI models to production and integrate them seamlessly with your existing systems and workflows.',
        activities: ['Production deployment', 'API integration', 'System integration', 'User training']
      },
      {
        title: 'Monitoring & Optimization',
        description: 'Continuous monitoring and optimization ensure your AI models maintain peak performance and adapt to changing conditions.',
        activities: ['Performance monitoring', 'Model retraining', 'Optimization updates', 'Support & maintenance']
      }
    ],
    technologies: [
      {
        category: 'Machine Learning',
        icon: 'Brain',
        tools: [
          { name: 'TensorFlow', expertise: 5 },
          { name: 'PyTorch', expertise: 5 },
          { name: 'Scikit-learn', expertise: 4 },
          { name: 'XGBoost', expertise: 4 }
        ]
      },
      {
        category: 'Deep Learning',
        icon: 'Zap',
        tools: [
          { name: 'Keras', expertise: 5 },
          { name: 'ONNX', expertise: 4 },
          { name: 'Hugging Face', expertise: 5 },
          { name: 'OpenAI GPT', expertise: 4 }
        ]
      },
      {
        category: 'Cloud Platforms',
        icon: 'Cloud',
        tools: [
          { name: 'AWS SageMaker', expertise: 5 },
          { name: 'Google AI Platform', expertise: 4 },
          { name: 'Azure ML', expertise: 4 },
          { name: 'MLflow', expertise: 4 }
        ]
      }
    ],
    timeline: [
      {
        phase: 'Project Initiation',
        duration: '1-2 weeks',
        description: 'Project kickoff, requirements gathering, and initial data assessment.',
        milestones: ['Project charter', 'Data audit', 'Technical roadmap']
      },
      {
        phase: 'Data Preparation',
        duration: '2-4 weeks',
        description: 'Data cleaning, preprocessing, and feature engineering for model training.',
        milestones: ['Clean dataset', 'Feature pipeline', 'Data validation']
      },
      {
        phase: 'Model Development',
        duration: '4-8 weeks',
        description: 'AI model design, development, and initial training with your data.',
        milestones: ['Model architecture', 'Initial training', 'Performance baseline']
      },
      {
        phase: 'Testing & Optimization',
        duration: '2-4 weeks',
        description: 'Model testing, validation, and performance optimization.',
        milestones: ['Model validation', 'Performance tuning', 'Security testing']
      },
      {
        phase: 'Deployment',
        duration: '1-3 weeks',
        description: 'Production deployment and system integration.',
        milestones: ['Production deployment', 'API integration', 'User training']
      },
      {
        phase: 'Monitoring & Support',
        duration: 'Ongoing',
        description: 'Continuous monitoring, maintenance, and optimization.',
        milestones: ['Monitoring setup', 'Performance tracking', 'Regular updates']
      }
    ],
    caseStudies: [
      {
        title: 'Predictive Maintenance AI for Manufacturing Giant',
        description: 'Reduced equipment downtime by 40% through intelligent predictive maintenance algorithms.',
        thumbnail: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200&h=150&fit=crop',
        industry: 'Manufacturing',
        improvement: '40% reduction'
      },
      {
        title: 'Customer Behavior Analysis for E-commerce Platform',
        description: 'Increased conversion rates by 25% using advanced customer behavior prediction models.',
        thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=200&h=150&fit=crop',
        industry: 'E-commerce',
        improvement: '25% increase'
      },
      {
        title: 'Fraud Detection System for Financial Services',
        description: 'Achieved 99.2% accuracy in real-time fraud detection while reducing false positives by 60%.',
        thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=200&h=150&fit=crop',
        industry: 'Financial Services',
        improvement: '99.2% accuracy'
      }
    ],
    teamExperts: [
      {
        name: 'Dr. Sarah Chen',
        role: 'Lead AI Scientist',
        expertise: 'Specializes in deep learning and neural network architectures with 8+ years of experience in AI research.',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
        skills: ['Deep Learning', 'Computer Vision', 'NLP', 'Research']
      },
      {
        name: 'Michael Rodriguez',
        role: 'Senior ML Engineer',
        expertise: 'Expert in production ML systems and MLOps with extensive experience in scalable AI deployments.',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        skills: ['MLOps', 'Cloud Deployment', 'System Architecture', 'Python']
      },
      {
        name: 'Dr. James Wilson',
        role: 'AI Strategy Consultant',
        expertise: 'Bridges business strategy with AI capabilities, helping organizations maximize AI ROI.',
        avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
        skills: ['AI Strategy', 'Business Analysis', 'ROI Optimization', 'Consulting']
      }
    ],
    capabilities: [
      {
        name: 'Machine Learning Algorithms',
        icon: 'Brain',
        skills: [
          {
            name: 'Supervised Learning',
            level: 'Expert',
            proficiency: 95,
            experience: '8+ years'
          },
          {
            name: 'Unsupervised Learning',
            level: 'Expert',
            proficiency: 90,
            experience: '6+ years'
          },
          {
            name: 'Reinforcement Learning',
            level: 'Advanced',
            proficiency: 85,
            experience: '4+ years'
          }
        ]
      },
      {
        name: 'Deep Learning Frameworks',
        icon: 'Zap',
        skills: [
          {
            name: 'TensorFlow/Keras',
            level: 'Expert',
            proficiency: 95,
            experience: '7+ years'
          },
          {
            name: 'PyTorch',
            level: 'Expert',
            proficiency: 92,
            experience: '5+ years'
          },
          {
            name: 'JAX/Flax',
            level: 'Advanced',
            proficiency: 80,
            experience: '2+ years'
          }
        ]
      },
      {
        name: 'Specialized AI Domains',
        icon: 'Target',
        skills: [
          {
            name: 'Natural Language Processing',
            level: 'Expert',
            proficiency: 90,
            experience: '6+ years'
          },
          {
            name: 'Computer Vision',
            level: 'Expert',
            proficiency: 88,
            experience: '5+ years'
          },
          {
            name: 'Time Series Analysis',
            level: 'Advanced',
            proficiency: 85,
            experience: '4+ years'
          }
        ]
      }
    ],
    faqs: [
      {
        question: 'How long does it typically take to develop a custom AI model?',
        answer: 'The development timeline varies based on complexity, data availability, and requirements. Simple models can be developed in 4-6 weeks, while complex enterprise solutions may take 3-6 months. We provide detailed timelines during the initial consultation phase.'
      },
      {
        question: 'What kind of data do you need to build an AI model?',
        answer: 'The data requirements depend on your specific use case. Generally, we need clean, relevant, and sufficient data that represents the problem you want to solve. We can work with structured data (databases, spreadsheets) or unstructured data (text, images, audio). Our team will assess your data during the discovery phase.'
      },
      {
        question: 'How do you ensure the AI model will work with our existing systems?',
        answer: 'We conduct thorough integration planning during the initial phases. Our models are designed with API-first architecture and can integrate with most existing systems. We also provide comprehensive documentation and support during the integration process.'
      },
      {
        question: 'What ongoing support do you provide after deployment?',
        answer: 'We offer comprehensive post-deployment support including performance monitoring, model retraining, bug fixes, and optimization updates. Our support packages range from basic monitoring to full managed services depending on your needs.'
      },
      {
        question: 'How do you handle data privacy and security?',
        answer: 'Data security and privacy are our top priorities. We implement enterprise-grade security measures, comply with relevant regulations (GDPR, HIPAA, etc.), and can work within your existing security frameworks. All data handling follows strict confidentiality protocols.'
      },
      {
        question: 'Can you help us understand the ROI of our AI investment?',
        answer: 'Absolutely. We work with you to define clear success metrics and KPIs before starting the project. We provide regular performance reports and help you measure the business impact of your AI implementation, including cost savings, efficiency gains, and revenue improvements.'
      }
    ],
    metrics: [
      {
        icon: 'TrendingUp',
        value: '95%',
        label: 'Success Rate',
        description: 'Project success rate'
      },
      {
        icon: 'Users',
        value: '150+',
        label: 'Models Deployed',
        description: 'Custom AI models in production'
      },
      {
        icon: 'Clock',
        value: '40%',
        label: 'Time Savings',
        description: 'Average process automation'
      },
      {
        icon: 'DollarSign',
        value: '300%',
        label: 'Average ROI',
        description: 'Return on AI investment'
      }
    ],
    achievements: [
      {
        title: 'Industry Recognition',
        description: 'AI Excellence Award 2024'
      },
      {
        title: 'Client Satisfaction',
        description: '98% client retention rate'
      },
      {
        title: 'Technical Expertise',
        description: 'Certified AI specialists'
      }
    ]
  };

  const handleConsultationClick = () => {
    const consultationSection = document.getElementById('consultation-form');
    if (consultationSection) {
      consultationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Breadcrumb />
      </div>

      {/* Hero Section */}
      <ServiceHero 
        service={serviceData} 
        onConsultationClick={handleConsultationClick}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-8 space-y-12">
            <ServiceOverview service={serviceData} />
            <SuccessMetrics service={serviceData} />
            <CapabilitiesMatrix service={serviceData} />
            <FAQSection service={serviceData} />
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-4 space-y-8">
            {/* Sticky Consultation Form */}
            <div 
              id="consultation-form"
              className={`transition-all duration-300 ${
                isSticky ? 'lg:sticky lg:top-24' : ''
              }`}
            >
              <ConsultationForm service={serviceData} />
            </div>
            
            <RelatedCaseStudies service={serviceData} />
            <TeamExpertise service={serviceData} />
          </div>
        </div>
      </div>

      {/* Related Services */}
      <RelatedServices currentService={serviceData} />

      {/* Floating CTA */}
      <ConsultationCTA variant="floating" context="services" />
    </div>
  );
};

export default IndividualServiceDetail;